import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import DreamerFieldCard from "@/components/controls/DreamerFieldCard";
import DreamerEntityMatchRow from "@/components/controls/DreamerEntityMatchRow";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import UserAvatar from "@/components/controls/UserAvatar";
import { UserData } from "@/entities/users/types";
import { getUsersOptions } from "@/entities/forms/helpers";
import { useProducts } from "@/entities/settings/hooks";
import { usePersonByEmail } from "@/entities/people/hooks";
import {
  useCheckCompanyDuplicates,
  DuplicateMatch
} from "@/pages/companies/hooks";
import { useCheckLeadDuplicates, CreateLeadWithDreamerRequest } from "../hooks";
import { useEntityFields } from "@/pages/settings/hooks";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import { excludePartnerFields } from "@/entities/dataTables/helpers";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import {
  createFormNewSectionsFromAPI,
  CreateFormNewContext,
  getFieldNameByDisplayType
} from "@/utils/entityFieldsUtils/formFields";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import useBoundStore from "@/services/store/BoundStore";
import { extractDomain, isPersonalEmailDomain } from "@/utils/domains";
import { emailRegex } from "@/utils/validation";

// Shorter than the manual tab's 500ms — the rows are only useful mid-typing. Exported so tests can't drift.
export const LOOKUP_DEBOUNCE_MS = 250;

export type DreamerStep = "email" | "details";

// The backend contract is fixed (CreateLeadWithDreamerRequest) — company/person/name come from enrichment.
const DETAIL_FIELD_ORDER = [
  "source",
  "partner",
  "productId",
  "salesExplorationMeetingDate",
  "sdrOwner",
  "aeOwner"
];
const REQUIRED_DETAIL_FIELDS = new Set(["source", "productId", "sdrOwner"]);

export type LeadDreamerDetails = Omit<CreateLeadWithDreamerRequest, "email">;

interface Props {
  email: string;
  setEmail: (email: string) => void;
  /** Set by the parent once Continue/submit has been attempted, so the field isn't red while typing. */
  showValidation: boolean;
  /** "email" shows just the field; "details" adds match rows and the lead-details form. */
  step: DreamerStep;
  userData: UserData;
  /** The details form — the parent's footer button submits it. */
  formRef: React.RefObject<HTMLFormElement>;
  onDetailsValidityChange: (isValid: boolean) => void;
  onSubmitDetails: (details: LeadDreamerDetails) => void;
}

/** "With Dreamer" tab: an email Dreamer resolves into person + company, plus the lead's own fields. */
const CreateLeadWithDreamer: React.FC<Props> = ({
  email,
  setEmail,
  showValidation,
  step,
  userData,
  formRef,
  onDetailsValidityChange,
  onSubmitDetails
}) => {
  const { t } = useTranslation();
  const [debouncedEmail, setDebouncedEmail] = useState("");

  const trimmedEmail = email.trim();
  const isInvalid =
    showValidation && trimmedEmail.length > 0 && !emailRegex.test(trimmedEmail);
  const isPersonalEmail =
    emailRegex.test(trimmedEmail) && isPersonalEmailDomain(trimmedEmail);

  useEffect(() => {
    if (!trimmedEmail) {
      setDebouncedEmail("");
      return;
    }
    const timer = setTimeout(
      () => setDebouncedEmail(trimmedEmail),
      LOOKUP_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [trimmedEmail]);

  // Rows only exist past Continue. Lead and person checks match the exact email, so they
  // run for any complete address; only the domain-based company lookup is meaningless for
  // personal-mail providers — those get the advisory instead of a company row.
  const isCompleteEmail =
    step === "details" && emailRegex.test(debouncedEmail);
  const isCompanyLookupEmail =
    isCompleteEmail && !isPersonalEmailDomain(debouncedEmail);
  const emailDomain = isCompanyLookupEmail
    ? (extractDomain(debouncedEmail) ?? "")
    : "";

  const leadCheck = useCheckLeadDuplicates(debouncedEmail, isCompleteEmail);
  const personLookup = usePersonByEmail(debouncedEmail, isCompleteEmail);
  const companyCheck = useCheckCompanyDuplicates(
    emailDomain,
    emailDomain.length > 0
  );

  const duplicateLeads = leadCheck.data?.leads ?? [];
  const matchedPerson = personLookup.data?.people?.[0];
  // Display-only: existence decisions gate on `matchedPerson` itself — a matched person
  // with no name on file must still read as "Already in CRM", not "Will be created".
  const matchedPersonName = matchedPerson
    ? [matchedPerson.firstName, matchedPerson.lastName]
        .filter(Boolean)
        .join(" ") || debouncedEmail
    : null;
  // Exact domain match decides existing-vs-created; similar matches aren't "the" company for this email.
  const matchedCompany: DuplicateMatch | undefined =
    companyCheck.data?.exactMatches?.[0];
  // Each row is data-gated on its own lookup (not isFetching, and not each other's):
  // "Will be created" must not flash mid-flight.
  const showPersonRow = isCompleteEmail && personLookup.data !== undefined;
  const showCompanyRow =
    isCompanyLookupEmail && companyCheck.data !== undefined;

  const users = useBoundStore((state) => state.users.entities);
  const { data: productsData, isFetched: isProductsFetched } = useProducts();
  const {
    data: entityFieldsResponse,
    isSuccess: isFieldsSuccess,
    isError: isFieldsError
  } = useEntityFields(EntityType.LEAD);
  // FormNew reads defaults once at mount, so it mounts once fields and products have each succeeded or failed.
  // A refetch after a failure resets status to pending. For fields that remounts a form with no inputs yet, which
  // brings the SDR owner default; products use isFetched instead, because by then the user may be filling the form.
  const isDetailsFormReady =
    (isFieldsSuccess || isFieldsError) && isProductsFetched;
  const enablePartnerships = useEnablePartnerships();
  const visibleFields = useMemo(
    () =>
      enablePartnerships
        ? entityFieldsResponse?.fields
        : excludePartnerFields(entityFieldsResponse?.fields ?? []),
    [entityFieldsResponse?.fields, enablePartnerships]
  );

  // The registry drives field types and options, exactly as the manual tab — only the subset differs.
  // Memoized so `detailSections` below only recomputes when its real inputs change.
  const baseSections = useMemo(() => {
    const detailsContext: CreateFormNewContext = {
      users,
      userData,
      selectedCompanyId: "",
      selectedPersonId: "",
      setSelectedCompanyId: () => {},
      setSelectedPersonId: () => {},
      setHasUserEnteredDealName: () => {},
      autoPopulateDealName: () => {},
      hasUserEnteredDealName: false,
      setView: () => {},
      entityType: EntityType.LEAD,
      showCreateOptionModal: false,
      newOptionName: "",
      currentFieldForNewOption: null,
      fieldResetKeys: {},
      setShowCreateOptionModal: () => {},
      setNewOptionName: () => {},
      setCurrentFieldForNewOption: () => {},
      handleCreateNewOption: async () => {}
    };
    return createFormNewSectionsFromAPI(visibleFields, t, detailsContext);
  }, [visibleFields, t, users, userData]);

  const partnerFieldName = getFieldNameByDisplayType(
    baseSections,
    FieldDisplayType.PARTNERS
  );

  const detailSections: FormSectionProps[] = useMemo(() => {
    const products = productsData?.products ?? [];
    const allFields = baseSections.flatMap((section) => section.fields ?? []);
    const orderedNames = DETAIL_FIELD_ORDER.map((name) =>
      name === "partner" ? partnerFieldName : name
    ).filter((name): name is string => Boolean(name));

    const fields = orderedNames.flatMap((name) => {
      const field = allFields.find((candidate) => candidate.name === name);
      if (!field) return [];

      const enhancedField = {
        ...field,
        labelPosition: "left" as const,
        layout: "flex" as const,
        // The Dreamer payload's own requirements, not the manual form's.
        required: REQUIRED_DETAIL_FIELDS.has(name)
      };
      if (field.name === "sdrOwner" || field.name === "aeOwner") {
        enhancedField.fieldActionProps = {
          fieldName: field.name,
          options: getUsersOptions(users),
          showSearch: true,
          renderIcon: (value: string | number) => (
            <UserAvatar userData={users[String(value)]} size="M" />
          )
        };
      }
      if (field.name === "sdrOwner") {
        enhancedField.defaultValue = userData.id;
      }
      if (field.name === "productId" && products?.length === 1) {
        enhancedField.defaultValue = products[0]?.id;
      }
      return [enhancedField];
    });

    return [{ id: "dreamer-lead-details", fields }];
  }, [baseSections, partnerFieldName, users, userData.id, productsData]);

  const handleDetailsSubmit = (values: Record<string, unknown>) => {
    onSubmitDetails({
      source: Number(values.source),
      productId: String(values.productId ?? ""),
      sdrOwner: String(values.sdrOwner ?? ""),
      aeOwner: (values.aeOwner as string) || null,
      partnerId: partnerFieldName
        ? ((values[partnerFieldName] as string) || null)
        : null,
      salesExplorationMeetingDate:
        (values.salesExplorationMeetingDate as string) || null
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <DreamerFieldCard
        hint={t("leads.createModal.withDreamer.hint")}
        label={t("leads.createModal.withDreamer.emailLabel")}
        placeholder={t("leads.createModal.withDreamer.emailPlaceholder")}
        value={email}
        onChange={setEmail}
        error={
          isInvalid ? t("leads.createModal.withDreamer.invalidEmail") : null
        }
        notice={
          isPersonalEmail
            ? t("leads.createModal.withDreamer.personalDomain")
            : null
        }
        dataTest="create-lead-dreamer-email"
      >
        {showPersonRow || showCompanyRow || duplicateLeads.length > 0 ? (
          <div className="mt-4 flex flex-col gap-3">
            {duplicateLeads.map((duplicateLead) =>
              duplicateLead.id && duplicateLead.name ? (
                <DreamerEntityMatchRow
                  key={duplicateLead.id}
                  entityType={EntityType.LEAD}
                  title={duplicateLead.name}
                  subtitle={t("dreamerCreate.leadEntity")}
                  status="existing"
                  badgeLabel={t("dreamerCreate.alreadyInCrm")}
                  dataTest="create-lead-dreamer-lead-match"
                />
              ) : null
            )}
            {showPersonRow ? (
              <DreamerEntityMatchRow
                entityType={EntityType.PERSON}
                title={
                  matchedPersonName ??
                  t("leads.createModal.withDreamer.noPersonMatch")
                }
                subtitle={
                  matchedPerson ? t("dreamerCreate.personEntity") : undefined
                }
                status={matchedPerson ? "existing" : "new"}
                badgeLabel={
                  matchedPerson
                    ? t("dreamerCreate.alreadyInCrm")
                    : t("dreamerCreate.willBeCreated")
                }
                dataTest="create-lead-dreamer-person-match"
              />
            ) : null}
            {showCompanyRow ? (
              <DreamerEntityMatchRow
                entityType={EntityType.COMPANY}
                title={
                  matchedCompany?.companyName ??
                  t("leads.createModal.withDreamer.noCompanyMatch")
                }
                subtitle={
                  matchedCompany ? t("dreamerCreate.companyEntity") : undefined
                }
                status={matchedCompany ? "existing" : "new"}
                badgeLabel={
                  matchedCompany
                    ? t("dreamerCreate.alreadyInCrm")
                    : t("dreamerCreate.willBeCreated")
                }
                dataTest="create-lead-dreamer-company-match"
              />
            ) : null}
          </div>
        ) : null}
      </DreamerFieldCard>

      {step === "details" ? (
        <>
          <div
            className="flex h-8 items-center justify-center gap-4"
            data-test="create-lead-dreamer-details-divider"
          >
            <div className="h-px flex-1 bg-border" />
            <Text variant="bodySecondary">
              {t("leads.createModal.withDreamer.leadDetails")}
            </Text>
            <div className="h-px flex-1 bg-border" />
          </div>
          {isDetailsFormReady ? (
            <FormNew
              sections={detailSections}
              isFormValid={onDetailsValidityChange}
              onSubmit={handleDetailsSubmit}
              formRef={formRef}
              shouldNotResetOnSubmit={true}
              data-test="create-lead-dreamer-details-form"
            />
          ) : (
            <div
              className="flex h-32 items-center justify-center"
              data-test="create-lead-dreamer-details-loading"
            >
              <Spinner />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default CreateLeadWithDreamer;
